import { Suspense, lazy } from 'react'
import { Routes, Route, Navigate, useSearchParams } from 'react-router-dom'
import { useAuthStore } from './store/authStore'
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute'
import { consumeAuthReturnTo, readReturnToFromSearch } from './utils/authReturnTo'
import LandingPage from './pages/Landing/LandingPage'
import DesignersLandingPage from './pages/Landing/DesignersLandingPage'
import MarketplaceLandingPage from './pages/Landing/MarketplaceLandingPage'
import BlogListPage from './pages/Landing/BlogListPage'
import BlogPostPage from './pages/Landing/BlogPostPage'
import './App.css'

// Lazy-loaded pages — loaded on demand
const Layout = lazy(() => import('./components/Layout/Layout'))
const LoginPage = lazy(() => import('./pages/Login/LoginPage'))
const RegisterPage = lazy(() => import('./pages/Register/RegisterPage'))
const VerifyPage = lazy(() => import('./pages/Verify/VerifyPage'))
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPassword/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('./pages/ResetPassword/ResetPasswordPage'))
const VkAuthCallbackPage = lazy(() => import('./pages/Auth/VkAuthCallbackPage'))
const HistoryPage = lazy(() => import('./pages/History/HistoryPage'))
const ChatPage = lazy(() => import('./pages/Chat/ChatPage'))
const StudioPage = lazy(() => import('./pages/Studio/StudioPage'))
const DashboardPage = lazy(() => import('./pages/Dashboard/DashboardPage'))
const VideoStudioPage = lazy(() => import('./pages/VideoStudio/VideoStudioPage'))
const AIAssistantPage = lazy(() => import('./pages/AIAssistant/AIAssistantPage'))
const AccountPage = lazy(() => import('./pages/Account/AccountPage'))
const FavoritesPage = lazy(() => import('./pages/Favorites/FavoritesPage'))
const HelpPage = lazy(() => import('./pages/Help/HelpPage'))
const ProductCardsPage = lazy(() => import('./pages/ProductCards/ProductCardsPage'))
const IllustrationPage = lazy(() => import('./pages/Illustration/IllustrationPage'))
const InfographicEditorPage = lazy(() => import('./pages/InfographicEditor/InfographicEditorPage'))
const IdeogramStudioPage = lazy(() => import('./pages/IdeogramV2/IdeogramStudioPage'))
const VideoContentPage = lazy(() => import('./pages/VideoContent/VideoContentPage'))
const AiAvatarPage = lazy(() => import('./pages/AiAvatar/AiAvatarPage'))
const BalancePage = lazy(() => import('./pages/Balance/BalancePage'))
const AIChatPage = lazy(() => import('./pages/AIChat/AIChatPage'))
const LibraryPage = lazy(() => import('./pages/Library/LibraryPage'))
const LibraryHubPage = lazy(() => import('./pages/LibraryHub/LibraryHubPage'))
const ToolsHubPage = lazy(() => import('./pages/ToolsHub/ToolsHubPage'))
const EduShortsPage = lazy(() => import('./pages/EduShorts/EduShortsPage'))
const SupportPage = lazy(() => import('./pages/Support/SupportPage'))
const SupportNewPage = lazy(() => import('./pages/Support/SupportNewPage'))
const SupportTicketPage = lazy(() => import('./pages/Support/SupportTicketPage'))
const ProfilePage = lazy(() => import('./pages/Profile/ProfilePage'))
const AdminSupportPage = lazy(() => import('./pages/Admin/AdminSupportPage'))
const AdminLayout = lazy(() => import('./pages/Admin/AdminLayout'))
const UsersPage = lazy(() => import('./pages/Admin/UsersPage'))
const ProvidersPage = lazy(() => import('./pages/Admin/ProvidersPage'))
const EmailSettingsPage = lazy(() => import('./pages/Admin/EmailSettingsPage'))
const BroadcastPage = lazy(() => import('./pages/Admin/BroadcastPage'))
const FalStatsPage = lazy(() => import('./pages/Admin/FalStatsPage'))
const PromoCodesPage = lazy(() => import('./pages/Admin/PromoCodesPage'))
const SubscriptionsPlansPage = lazy(() => import('./pages/Admin/SubscriptionsPlansPage'))
const AssistantsPage = lazy(() => import('./pages/Admin/AssistantsPage'))
const AdminAssistantDetail = lazy(() => import('./pages/Admin/AdminAssistantDetail'))
const AssistantsSkillsPage = lazy(() => import('./pages/Admin/AssistantsSkillsPage'))
const FinancePage = lazy(() => import('./pages/Admin/FinancePage'))
const PricingPage = lazy(() => import('./pages/Admin/PricingPage'))
const SecurityPage = lazy(() => import('./pages/Admin/SecurityPage'))
const YooKassaPage = lazy(() => import('./pages/Admin/YooKassaPage'))
const InvoicesPage = lazy(() => import('./pages/Admin/InvoicesPage'))
const PaymentResultPage = lazy(() => import('./pages/Payments/PaymentResultPage'))
const PublicOfferPage = lazy(() => import('./pages/PublicOffer/PublicOfferPage'))
const ErrorsPage = lazy(() => import('./pages/Admin/ErrorsPage'))
const IncidentsPage = lazy(() => import('./pages/Admin/IncidentsPage'))
const LogsPage = lazy(() => import('./pages/Admin/LogsPage'))
const AnalyticsPage = lazy(() => import('./pages/Admin/AnalyticsPage'))
const BlogAdminPage = lazy(() => import('./pages/Admin/BlogAdminPage'))
const BlogAdminEditPage = lazy(() => import('./pages/Admin/BlogAdminEditPage'))

// Minimal loading state while chunk loads
function PageFallback() {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      background: 'var(--md-sys-color-background, #FFFBFE)',
      color: 'var(--md-sys-color-on-background, #1C1B1F)',
      fontSize: 14,
      fontFamily: 'system-ui, sans-serif',
    }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{
          width: 32, height: 32,
          border: '2px solid var(--md-sys-color-outline-variant, #CAC4D0)',
          borderTopColor: '#fff',
          borderRadius: '50%',
          animation: 'kmin-spin 0.6s linear infinite',
          margin: '0 auto 16px',
        }} />
        Загрузка…
        <style>{`@keyframes kmin-spin { to { transform: rotate(360deg) } }`}</style>
      </div>
    </div>
  )
}

function AuthenticatedRedirect() {
  const [searchParams] = useSearchParams()
  const fromQuery = readReturnToFromSearch(searchParams.toString())
  const target = fromQuery || consumeAuthReturnTo('/dashboard')
  return <Navigate to={target} replace />
}

function App() {
  const { isAuthenticated } = useAuthStore()

  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={isAuthenticated ? <AuthenticatedRedirect /> : <LoginPage />}
        />
        <Route
          path="/register"
          element={isAuthenticated ? <AuthenticatedRedirect /> : <RegisterPage />}
        />
        <Route
          path="/verify"
          element={<VerifyPage />}
        />
        <Route
          path="/forgot-password"
          element={<ForgotPasswordPage />}
        />
        <Route
          path="/reset-password"
          element={<ResetPasswordPage />}
        />
        <Route
          path="/auth/vk/callback"
          element={isAuthenticated ? <Navigate to="/dashboard" /> : <VkAuthCallbackPage />}
        />

        {/* Landing page (standalone, no layout) */}
        <Route
          path="/"
          element={isAuthenticated ? <Navigate to="/dashboard" /> : <LandingPage />}
        />
        <Route path="/designers" element={<DesignersLandingPage />} />
        <Route path="/marketplace" element={<MarketplaceLandingPage />} />
        <Route path="/blog" element={<BlogListPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />



        {/* Payment redirect pages */}
        <Route path="/payment/success/:orderId" element={<PaymentResultPage />} />
        <Route path="/payment/fail/:orderId" element={<PaymentResultPage />} />

        {/* App shell — browse without auth; mutations gated per page */}
        <Route element={<Layout />}>
          <Route path="/chat" element={<ChatPage />} />
          <Route path="/chat/:chatId" element={<ChatPage />} />
          <Route path="/ideogram" element={<IdeogramStudioPage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/studio" element={<StudioPage />} />
          <Route path="/tariffs" element={<Navigate to="/balance" replace />} />
          <Route path="/balance" element={<BalancePage />} />
          <Route path="/ai-assistant" element={<AIAssistantPage />} />
          <Route path="/ai-assistant/:assistantId" element={<AIChatPage />} />
          <Route path="/tools" element={<ToolsHubPage />} />
          <Route path="/edu-shorts" element={<EduShortsPage />} />
          <Route path="/library" element={<LibraryHubPage />} />
          <Route path="/library/examples" element={<LibraryPage />} />
          <Route path="/product-cards" element={<ProductCardsPage />} />
          <Route path="/illustration" element={<IllustrationPage />} />
          <Route path="/infographic-editor" element={<InfographicEditorPage />} />
          <Route path="/video" element={<VideoStudioPage />} />
          <Route path="/video/:sessionId" element={<VideoStudioPage />} />
          <Route path="/video-content" element={<VideoContentPage />} />
          <Route path="/ai-avatar/:tab?" element={<AiAvatarPage />} />
          <Route path="/help" element={<HelpPage />} />
          <Route path="/public-offer" element={<PublicOfferPage />} />

          <Route path="/account" element={<ProtectedRoute><AccountPage /></ProtectedRoute>} />
          <Route path="/history" element={<ProtectedRoute><HistoryPage /></ProtectedRoute>} />
          <Route path="/favorites" element={<ProtectedRoute><FavoritesPage /></ProtectedRoute>} />
          <Route path="/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
          <Route path="/support/new" element={<ProtectedRoute><SupportNewPage /></ProtectedRoute>} />
          <Route path="/support/:id" element={<ProtectedRoute><SupportTicketPage /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />

          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<Navigate to="/admin/users" replace />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="providers" element={<ProvidersPage />} />
            <Route path="email" element={<EmailSettingsPage />} />
            <Route path="broadcast" element={<BroadcastPage />} />
            <Route path="pricing" element={<PricingPage />} />
            <Route path="subscriptions" element={<SubscriptionsPlansPage />} />
            <Route path="security" element={<SecurityPage />} />
            <Route path="yookassa" element={<YooKassaPage />} />
            <Route path="invoices" element={<InvoicesPage />} />
            <Route path="support" element={<AdminSupportPage />} />
            <Route path="fal" element={<FalStatsPage />} />
            <Route path="finance" element={<FinancePage />} />
            <Route path="logs" element={<LogsPage />} />
            <Route path="errors" element={<ErrorsPage />} />
            <Route path="incidents" element={<IncidentsPage />} />
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="promo-codes" element={<PromoCodesPage />} />
            <Route path="blog" element={<BlogAdminPage />} />
            <Route path="blog/:id" element={<BlogAdminEditPage />} />
            <Route path="assistants" element={<AssistantsPage />} />
            <Route path="assistants/:id" element={<AdminAssistantDetail />} />
            <Route path="skills" element={<AssistantsSkillsPage />} />
          </Route>
        </Route>
      </Routes>
    </Suspense>
  )
}

export default App
